import * as React from 'react';
import { Toast } from './Toast';
import { ToastLayer } from './ToastLayer';
// Generate unique IDs for toasts
let toastIdCounter = 0;
const generateToastId = ()=>`toast-provider-id-${toastIdCounter++}`;
const ToastContext = /*#__PURE__*/ React.createContext(null);
export const ToastProvider = ({ children })=>{
    const [toasts, setToasts] = React.useState([]);
    const show = React.useCallback(({ placement = 'bottom', ...options })=>{
        const id = generateToastId();
        const newToast = {
            ...options,
            id,
            isOpen: false,
            placement
        };
        setToasts((prev)=>[
                ...prev,
                newToast
            ]);
        /**
       * TODO: use startTransition when we migrate to React 19
       * We need firstly to render ToastLayer and then update children
       * so appearing animation is smooth and we do not affect render count
       * for RootLayer
       */ requestAnimationFrame(()=>{
            setToasts((prev)=>prev.map((toast)=>toast.id === id ? {
                        ...toast,
                        isOpen: true
                    } : toast));
        });
        return id;
    }, []);
    const update = React.useCallback((id, options)=>{
        setToasts((prev)=>prev.map((toast)=>toast.id === id ? {
                    ...toast,
                    ...options
                } : toast));
    }, []);
    const dismiss = React.useCallback((id)=>{
        setToasts((prev)=>{
            const newToasts = prev.map((toast)=>toast.id === id ? {
                    ...toast,
                    isOpen: false
                } : toast);
            setTimeout(()=>{
                setToasts((v)=>v.filter((toast)=>toast.id !== id));
            }, 1000);
            return newToasts;
        });
    }, []);
    const dismissAll = React.useCallback(()=>{
        setToasts([]);
    }, []);
    const handleToastDismiss = React.useCallback((toast)=>()=>{
            var _toast_onDismiss;
            dismiss(toast.id);
            (_toast_onDismiss = toast.onDismiss) === null || _toast_onDismiss === void 0 ? void 0 : _toast_onDismiss.call(toast);
        }, [
        dismiss
    ]);
    const value = React.useMemo(()=>({
            show,
            update,
            dismiss,
            dismissAll
        }), [
        show,
        update,
        dismiss,
        dismissAll
    ]);
    const topToasts = React.useMemo(()=>toasts.filter((t)=>t.placement === 'top'), [
        toasts
    ]);
    const bottomToasts = React.useMemo(()=>toasts.filter((t)=>t.placement === 'bottom'), [
        toasts
    ]);
    const hasTopLayer = topToasts.length > 0;
    const hasBottomLayer = bottomToasts.length > 0;
    return /*#__PURE__*/ React.createElement(ToastContext.Provider, {
        value: value
    }, children, hasTopLayer ? /*#__PURE__*/ React.createElement(ToastLayer, {
        placement: "top"
    }, topToasts.map((toast)=>/*#__PURE__*/ React.createElement(Toast, {
            ...toast,
            key: toast.id,
            onDismiss: handleToastDismiss(toast)
        }, toast.message))) : null, hasBottomLayer ? /*#__PURE__*/ React.createElement(ToastLayer, {
        placement: "bottom"
    }, bottomToasts.map((toast)=>/*#__PURE__*/ React.createElement(Toast, {
            ...toast,
            key: toast.id,
            onDismiss: handleToastDismiss(toast)
        }, toast.message))) : null);
};
/**
 * Hook to show, dismiss, and manage toasts.
 *
 * Must be used within a ToastProvider.
 *
 * @example
 * ```tsx
 * const toast = useToast();
 *
 * // Show a toast and get its ID
 * const id = toast.show({
 *   message: 'Operation successful!',
 *   type: 'neutral',
 * });
 *
 * // Update an existing toast
 * toast.update(id, {
 *   message: 'Updated message',
 *   type: 'error',
 * });
 *
 * // Dismiss a specific toast
 * toast.dismiss(id);
 *
 * // Dismiss all toasts
 * toast.dismissAll();
 * ```
 */ export const useToast = ()=>{
    const context = React.useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider. Please ensure you have wrapped your application in a <RootLayer /> component.');
    }
    return context;
};

//# sourceMappingURL=ToastProvider.js.map